
  import { Component, Watch } from 'vue-property-decorator'
  import { plainToInstance } from 'class-transformer'
  import BaseForm from '@/components/forms/view/BaseForm.vue'
  import { Form } from '@/entities/public/Resource/metadata'
  import { fixAmountFormatted, isValidNumber, stringifySafe } from '@/utils/general'

  import LinkedPerson from '@/components/forms/fields/LinkedPerson.vue'
  import GFiles from '@/components/core/files/GFiles.vue'
  import GRadioButton from '@/components/core/input/GRadioButton.vue'

  import { GForm } from '@/components/forms/GForm'
  import { Stock } from '@/entities/sales'
  import GCostField from '@/components/core/input/GCostField.vue'

@Component({
  components: { GCostField, GRadioButton, GFiles, LinkedPerson, BaseForm },
  methods: { fixAmountFormatted },
  computed: {},
})
  export default class SupervisorStockPriceForm extends GForm {
  stock: Stock = plainToInstance(Stock, {})
  message = ''
  processInspectionId = null
  showDetail = false
  showFields = false
  disabledPrice = false
  reserveAlert = false
  sellingProcess = false
  categoryComponentMaintenance = null
  categoryComponentIdentification = null
  priceList = null
  hasFiles = false
  errorMessage = {
    mileage: '',
  }

  title = ''

  formData = {
    priceList: null,
    discount: null,
    renewalBonus: null,
    financingBonus: null,
    commentForSalesTeam: '',
    commentForPublication: '',
  }

  fields = {
    button1: {
      label: '',
      link: '',
    },
    button2: {
      label: '',
      link: '',
    },
  }

  metadata = {}
  metadataCollection = {}

  async mounted () {
    await this.setMetadata()
    const { uid, id, title, metadataCollection } = this
    const enablementId = uid || id

    if (isValidNumber(enablementId)) {
      await this.getStockInfo(enablementId)
    }

    if (!this.isBreadCrumbPresent(title)) {
      this.setFormCrumbs(metadataCollection, title, Boolean(enablementId))
    }

    this.displayDetail()
  }

  async setMetadata () {
    const { metadata } = this.getForm('Stock', 'supervisor_stock_price')
    const { form, fields } = metadata as Form
    this.title = form.title
    this.metadataCollection = metadata

    this.fields.button1.label = fields.button1.properties.label
    this.fields.button1.link = fields.button1.properties.url
    this.fields.button2.label = fields.button2.properties.label
    this.fields.button2.link = fields.button2.properties.url
  }

  async getStockInfo (id) {
    this.stock = await this.fetchData({
      query: { name: 'fetch', model: 'Stock', params: { id } },
      force: true,
    })

    this.stock.transferCost = await this.calculateTransfer(id)

    if (this.stock.auto?.generation?.id) {
      const generation = await this.fetchData({
        query: { name: 'find', model: 'Generation' },
        filter: { id: { _eq: this.stock.auto.generation.id } },
        force: true,
      })
      this.stock.auto.generation = generation?.[0]
    }

    const prices = await this.fetchData({
      query: { name: 'find', model: 'Price' },
      filter: { _and: [{ id_stock: { _eq: id } }, { expiration: { _is_null: true } }] },
      force: true,
    })

    this.priceList = prices?.[0]?.amount
    await this.checkValidations(id)

    await this.getStockFiles(id)
  }

  async checkValidations (id) {
    const exist = await this.fetchData({
      query: { name: 'find', model: 'Stock' },
      filter: {
        _and: [
          {
            deals: {
              _and: [
                {
                  id_closing_reason: {
                    _is_null: true,
                  },
                },
                {
                  payments: {
                    payment_type: {
                      name: {
                        _eq: 'financing',
                      },
                    },
                  },
                },
              ],
            },
          },
          {
            id: {
              _eq: id,
            },
          },
        ],
      },
      force: true,
    })

    this.disabledPrice = Boolean(exist.length)

    const reserveExist = await this.fetchData({
      query: { name: 'find', model: 'Stock' },
      filter: {
        _and: [
          {
            deals: {
              _and: [
                {
                  id_closing_reason: {
                    _is_null: true,
                  },
                },
                {
                  sale_orders: {
                    reserve: {
                      id_closing_reason: {
                        _is_null: true,
                      },
                    },
                  },
                },
              ],
            },
          },
          {
            id: {
              _eq: id,
            },
          },
        ],
      },
      force: true,
    })
    this.reserveAlert = Boolean(reserveExist.length)

    const sellingProcess = await this.fetchData({
      query: { name: 'find', model: 'Stock' },
      filter: {
        _and: [
          {
            deals: {
              _and: [
                {
                  id_closing_reason: { _is_null: true },
                },
                {
                  sale_orders: {
                    _and: [
                      {
                        id_closing_reason: { _is_null: true },
                      },
                      {
                        status: {
                          status: {
                            name: {
                              _neq: 'pending',
                            },
                          },
                        },
                      },
                    ],
                  },
                }],
            },
          },
          {
            id: { _eq: id },
          }],
      },
      force: true,
    })
    this.sellingProcess = Boolean(sellingProcess?.length)
  }

  async getStockFiles (uid) {
    const photos = await this.fetchData({
      query: { name: 'find', model: 'FileParameter' },
      filter: { _and: [{ process: { table_name: { _eq: 'stock' } } }, { file_type: { name: { _eq: 'photo' } } }] },
    })

    const fileParametersPhoto = photos.sort((a, b) => a.visibility?.form?.order - b?.visibility?.form?.order)
    const handleComponentChange = async parameter => {
      const photos = await this.fetchData({
        query: { name: 'find', model: 'FileProcess' },
        filter: {
          _and: [
            { id_process_record: { _eq: uid } },
            { id_file_parameter: { _eq: parameter.id } },
          ],
        },
        force: true,
      })

      if (photos.length) {
        this.hasFiles = true
      }
    }

    for (const parameter of [...fileParametersPhoto]) {
      await handleComponentChange(parameter)
    }
  }

  displayDetail () {
    const { stock, metadataCollection } = this

    this.metadata = {
      data: stock,
      metadata: metadataCollection,
    }

    this.showDetail = Boolean(stock.id)
  }

  async send () {
    if (!this.$refs.form.validate()) {
      return
    }
    this.loadingForm = true
    await this.sendPrices()
    await this.publishStock(this.stock.id)
    this.loadingForm = false
    await this.close()
  }

  async sendPrices () {
    const { formData, stock, sellingProcess } = this

    const newPrice = await this.pushData({
      model: 'Price',
      fields: {
        id_stock: stock.id,
        amount: formData.priceList,
        margin: formData.discount,
        comment: formData.commentForPublication,
        post_comment: formData.commentForSalesTeam,
      },
    })

    const types = await this.fetchData({
      query: { name: 'find', model: 'BonusType' },
      force: true,
    })

    if (formData.renewalBonus) {
      await this.pushData({
        model: 'Bonus',
        fields: {
          id_price: newPrice.id,
          amount: formData.renewalBonus,
          id_bonus_type: types.find(type => type.name === 'renewal').id,
        },
      })
    }

    if (formData.financingBonus) {
      await this.pushData({
        model: 'Bonus',
        fields: {
          id_price: newPrice.id,
          amount: formData.financingBonus,
          id_bonus_type: types.find(type => type.name === 'financing').id,
        },
      })
    }

    await this.updateOtherSalesOrderItem(stock.id, formData.priceList)
    console.log(sellingProcess)
  }

  async updateOtherSalesOrderItem (stockId, amount) {
    const salesOrderItems = await this.fetchData({
      query: { name: 'find', model: 'SaleOrderItem' },
      filter: {
        _and: [
          {
            sale_order: {
              _and: [
                { id_closing_reason: { _is_null: true } },
                { deal: { id_stock: { _eq: stockId } } },
              ],
            },
          },
          {
            item_type: { name: { _eq: 'product' } },
          },
        ],
      },
      force: true,
    })

    await Promise.all(salesOrderItems.map(async item => {
      await this.pushData({
        model: 'SaleOrderItem',
        fields: {
          id: item.id,
          amount,
        },
      })
    }))
  }

  displayFields () {
    const { formData: { priceList } } = this

    if (priceList?.length > 6 && isValidNumber(priceList)) {
      this.showFields = true
    }
  }

  get change () {
    const { formData, showFields } = this

    return stringifySafe([formData, showFields])
  }

  get officialSite () {
    const { stock } = this

    return stock?.auto?.officialSite || ''
  }

  get hasPrice () {
    const { priceList } = this.formData

    return Boolean(priceList)
  }

  get filesSet () {
    const { hasFiles } = this
    return hasFiles
  }

  get displayMessage () {
    const { hasPrice, filesSet } = this
    return hasPrice && filesSet
  }

  @Watch('formData.priceList', { immediate: true })
  onPriceListChange () {
    this.displayFields()
  }
  }
